import { ExternalLink, Globe2, ShieldCheck } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * /subprocessadores — pagina pública (sem login) listando todos os
 * terceiros que processam dados pessoais em nome do Octa Pontos.
 *
 * Conformidade LGPD: lista pública e atualizada permite que titulares
 * conhecam os subprocessadores antes de aceitar os termos.
 *
 * Sprint CCCC.
 */

interface Subprocessador {
  nome: string;
  servico: string;
  dadosTratados: string;
  localizacao: string;
  dpa?: string;
  privacy?: string;
  conformidade?: string[];
}

const PROVEDORES_INFRA: Subprocessador[] = [
  {
    nome: 'Supabase',
    servico: 'Banco Postgres 17, autenticação, storage, edge functions, realtime',
    dadosTratados:
      'Cadastro, veículos, transações, logs auditoria, conteudo storage (CRLV, evidencias, relatorios LGPD)',
    localizacao: 'AWS sa-east-1 (São Paulo, Brasil)',
    dpa: 'https://supabase.com/legal/dpa',
    privacy: 'https://supabase.com/privacy',
    conformidade: ['SOC 2 Type II', 'GDPR', 'ISO 27001'],
  },
  {
    nome: 'Cloudflare',
    servico: 'CDN, Pages (web hosting), Workers, R2 (storage), KV (cache)',
    dadosTratados: 'Trafego web (cookies sessao, IP), backups cifrados, contadores rate-limit',
    localizacao: 'Global (R2 sa-east-1)',
    dpa: 'https://www.cloudflare.com/cloudflare-customer-dpa/',
    privacy: 'https://www.cloudflare.com/privacypolicy/',
    conformidade: ['SOC 2 Type II', 'ISO 27001', 'GDPR'],
  },
];

const PROVEDORES_PAGAMENTO: Subprocessador[] = [
  {
    nome: 'Asaas',
    servico: 'Processamento de pagamentos (PIX, cartao, boleto)',
    dadosTratados: 'CPF/CNPJ, dados de pagamento, cobrancas',
    localizacao: 'Brasil (regulada pelo Banco Central)',
    dpa: 'https://www.asaas.com/contratos',
    privacy: 'https://www.asaas.com/politica-de-privacidade',
    conformidade: ['LGPD', 'PCI DSS Level 1'],
  },
];

const PROVEDORES_NOTIFICACAO: Subprocessador[] = [
  {
    nome: 'uazapi',
    servico: 'Envio de mensagens via WhatsApp Business API',
    dadosTratados: 'Número de WhatsApp, conteudo da mensagem (template parametrizado)',
    localizacao: 'Brasil',
    privacy: 'https://uazapi.com/privacidade',
  },
  {
    nome: 'Firebase Cloud Messaging (FCM)',
    servico: 'Envio de push notifications mobile (iOS/Android)',
    dadosTratados: 'Token de dispositivo, payload da notificação (titulo, corpo)',
    localizacao: 'Global (Google)',
    dpa: 'https://firebase.google.com/terms/data-processing-terms',
    privacy: 'https://policies.google.com/privacy',
  },
];

const PROVEDORES_HARDWARE: Subprocessador[] = [
  {
    nome: 'Intelbras LPR',
    servico: 'Camera com leitura de placa (ANPR/LPR)',
    dadosTratados: 'Imagem da placa, timestamp, posição da camera',
    localizacao: 'Brasil',
    privacy: 'https://www.intelbras.com/pt-br/sobre/politica-de-privacidade',
  },
];

const PROVEDORES_OBSERVABILIDADE: Subprocessador[] = [
  {
    nome: 'Sentry (opcional, com consentimento)',
    servico: 'Monitoramento de erros front-end e back-end',
    dadosTratados: 'Stack traces, user-agent, URL (sem PII — email/username são removidos)',
    localizacao: 'EUA / EU (configurado por projeto)',
    dpa: 'https://sentry.io/legal/dpa/',
    privacy: 'https://sentry.io/privacy/',
    conformidade: ['SOC 2 Type II', 'ISO 27001'],
  },
];

function CategoriaCards({
  titulo,
  icon: Icon,
  items,
}: {
  titulo: string;
  icon: typeof Globe2;
  items: Subprocessador[];
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="text-primary h-5 w-5" />
        <h2 className="text-lg font-semibold">{titulo}</h2>
      </div>
      <div className="space-y-3">
        {items.map((s) => (
          <Card key={s.nome}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{s.nome}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <p>
                <strong>Serviço:</strong> {s.servico}
              </p>
              <p>
                <strong>Dados tratados:</strong> {s.dadosTratados}
              </p>
              <p>
                <strong>Localizacao:</strong> {s.localizacao}
              </p>
              {s.conformidade && s.conformidade.length > 0 && (
                <p>
                  <strong>Conformidade:</strong> {s.conformidade.join(', ')}
                </p>
              )}
              <div className="flex flex-wrap gap-3 pt-2">
                {s.dpa && (
                  <a
                    href={s.dpa}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
                  >
                    DPA <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {s.privacy && (
                  <a
                    href={s.privacy}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary inline-flex items-center gap-1 text-xs hover:underline"
                  >
                    Politica de privacidade <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

export function SubprocessadoresPage() {
  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 md:p-8">
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-primary h-6 w-6" />
          <h1 className="text-2xl font-bold">Subprocessadores</h1>
        </div>
        <p className="text-muted-foreground text-sm">
          Lista publica e atualizada de todos os terceiros que processam dados pessoais em nome do
          Octa Pontos. Conforme LGPD art. 39, alterações nesta lista são notificadas com 30 dias de
          antecedência.
        </p>
        <p className="text-muted-foreground text-xs">Última atualização: 2026-05-13</p>
      </header>

      <CategoriaCards titulo="Infraestrutura" icon={Globe2} items={PROVEDORES_INFRA} />
      <CategoriaCards titulo="Pagamentos" icon={Globe2} items={PROVEDORES_PAGAMENTO} />
      <CategoriaCards titulo="Notificações" icon={Globe2} items={PROVEDORES_NOTIFICACAO} />
      <CategoriaCards titulo="Hardware" icon={Globe2} items={PROVEDORES_HARDWARE} />
      <CategoriaCards titulo="Observabilidade" icon={Globe2} items={PROVEDORES_OBSERVABILIDADE} />

      <footer className="text-muted-foreground border-t pt-4 text-xs">
        <p>
          Para sub-subprocessadores (terceiros dos terceiros) e DPA do Octa Pontos, consulte{' '}
          <a href="/politica-privacidade" className="text-primary hover:underline">
            politica de privacidade
          </a>
          .
        </p>
      </footer>
    </div>
  );
}
